import type Highcharts from 'highcharts';
import moment from 'moment';
import MarketInsightBookingCurve from '@app/model/market_insight/MarketInsightBookingCurve';
import { marketBookingCurveColors } from '@app/model/market_insight/MarketInsightBookingCurveColors';

const WINDOW_DAYS = 120;
const MARKER_RADIUS = 4;
const END_LABEL_GAP = 2;
// The rate axis spans at least this share below and above the newest rate, so that a 1 % move stays a small step.
const RATE_AXIS_SPAN = 0.1;

// Highcharts keeps the data label on its point and the label position on the label. The public
// types name neither, so the two fields are typed here.
type EndLabel = Highcharts.SVGElement & { translateY: number };
type EndPoint = Highcharts.Point & { dataLabel?: EndLabel };

export const buildOptions = (curve: MarketInsightBookingCurve | null | undefined, statDate: string): Highcharts.Options => {
	const points = curve?.points ?? [];
	// The store keeps the Market Availability (rooms left over capacity). The chart shows the Market
	// Occupancy, one minus that rate, so the line reads as the Market number of the Occupancy card.
	const occupancy = points.map(point => [moment.utc(point.snapshotDay).valueOf(), (1 - point.availabilityRate) * 100] as [number, number]);
	const rate = points.map(point => [moment.utc(point.snapshotDay).valueOf(), point.marketRate] as [number, number | null]);
	const last = curve ? moment.utc(curve.newestSnapshotDay).valueOf() : undefined;
	// The window is fixed: the last WINDOW_DAYS scan days up to the newest scan. A stay date that the
	// scan reached later shows a shorter line that starts inside the window. No backfill.
	const windowStart = last === undefined ? undefined : moment.utc(last).subtract(WINDOW_DAYS - 1, 'days').valueOf();
	const currency = curve ? new Intl.NumberFormat('en', { style: 'currency', currency: curve.currency, currencyDisplay: 'narrowSymbol' }).formatToParts(0).find(part => part.type === 'currency')?.value ?? curve.currency : '';
	const hasRate = points.some(point => point.marketRate !== null);
	const newestRate = points.findLast(point => point.marketRate !== null)?.marketRate ?? undefined;
	const arrival = moment.utc(statDate);

	return {
		// Without a rate there is no right axis; the right spacing keeps the end dot and the "today" line
		// inside the card.
		chart: { height: 220, spacing: [8, hasRate ? 0 : 8, 0, 0], style: { fontFamily: 'inherit' }, events: { render: function() { separateEndLabels(this); } } },
		title: { text: '' },
		credits: { enabled: false },
		legend: { enabled: true, align: 'left', verticalAlign: 'top', symbolWidth: 12, itemStyle: { fontSize: '12px', fontWeight: '400', color: marketBookingCurveColors.text } },
		xAxis: {
			type: 'datetime',
			min: windowStart, max: last,
			tickPixelInterval: 110,
			dateTimeLabelFormats: { day: '%d/%m/%Y', week: '%d/%m/%Y', month: '%d/%m/%Y' },
			labels: { autoRotation: undefined, style: { fontSize: '11px', color: marketBookingCurveColors.text } },
			lineColor: marketBookingCurveColors.grid,
			tickWidth: 0,
			// The "today" text sits above the plot: a full market ends the occupancy line in the top corner.
			plotLines: last === undefined ? [] : [{
				value: last, dashStyle: 'ShortDash', color: marketBookingCurveColors.muted, width: 1,
				label: { text: 'today', align: 'right', textAlign: 'right', rotation: 0, x: -6, y: -4, style: { fontSize: '10px', color: marketBookingCurveColors.muted } },
			}],
		},
		yAxis: [{
			min: 0, max: 100, tickAmount: 5, title: { text: undefined },
			labels: { format: '{value}%', style: { fontSize: '11px', color: marketBookingCurveColors.text } },
			gridLineColor: marketBookingCurveColors.grid,
		}, {
			// Whole-euro ticks only: the labels round to whole euros, so a narrow rate range with half-euro ticks
			// printed the same label twice (€106, €106, €107 on 2026-09-30).
			// At least ±10 % around the newest rate: fitted to the data only, a change under 1 % filled the whole height.
			opposite: true, title: { text: undefined }, gridLineWidth: 0, allowDecimals: false,
			softMin: newestRate === undefined ? undefined : newestRate * (1 - RATE_AXIS_SPAN),
			softMax: newestRate === undefined ? undefined : newestRate * (1 + RATE_AXIS_SPAN),
			labels: { format: `${currency}{value:.0f}`, style: { fontSize: '11px', color: marketBookingCurveColors.text } },
		}],
		tooltip: {
			shared: true,
			useHTML: false,
			formatter: function() {
				const day = moment.utc(this.x as number);
				const before = arrival.diff(day, 'days');
				const lines = [`<b>${day.format('DD MMMM YYYY')} · ${before} days before arrival</b>`];
				(this.points ?? []).forEach(point => {
					const value = point.series.name === 'Market rate' ? `${currency}${Math.round(point.y as number)}` : `${Math.round(point.y as number)}%`;
					lines.push(`<span style="color:${point.color}">●</span> ${point.series.name} ${value}`);
				});
				return lines.join('<br/>');
			},
		},
		plotOptions: {
			series: { marker: { enabled: false, symbol: 'circle', radius: MARKER_RADIUS }, lineWidth: 2, connectNulls: false, states: { hover: { lineWidthPlus: 0 } } },
		},
		series: [{
			type: 'area',
			name: 'Market occupancy',
			color: marketBookingCurveColors.occupancy,
			yAxis: 0,
			data: withLastMarker(occupancy, '{y:.0f}%'),
			fillColor: {
				linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
				stops: [[0, 'rgba(59, 130, 246, 0.16)'], [1, 'rgba(59, 130, 246, 0)']],
			},
		}, {
			type: 'line',
			name: 'Market rate',
			color: marketBookingCurveColors.rate,
			yAxis: 1,
			data: withLastMarker(rate, `${currency}{y:.0f}`),
			visible: hasRate,
			showInLegend: hasRate,
		}],
	};
};

// The last point of a line carries a marker and its value; the others stay plain.
const withLastMarker = (data: [number, number | null][], format: string): Highcharts.PointOptionsObject[] => data.map((pair, index) => {
	const point: Highcharts.PointOptionsObject = { x: pair[0], y: pair[1] };
	if (index === data.findLastIndex(item => item[1] !== null)) {
		point.marker = { enabled: true };
		point.dataLabels = { enabled: true, allowOverlap: true, align: 'right', x: -8, format: format, style: { fontSize: '11px', fontWeight: '600', textOutline: `2px ${marketBookingCurveColors.paper}` } };
	}
	return point;
});

// Both end labels sit above their dots. When the two lines end at about the same height, the labels
// meet and the lower value reads as the value of the upper line. The label of the lower dot then goes
// below its dot; at the bottom of the plot the label of the upper dot goes up instead. The chart calls
// this after each draw, and Highcharts places the labels again before each draw.
export const separateEndLabels = (chart: Highcharts.Chart) => {
	const ends = chart.series
		.filter(series => series.visible)
		.map(series => (series.points as EndPoint[]).findLast(point => typeof point.y === 'number'))
		.filter((point): point is EndPoint & { dataLabel: EndLabel, plotY: number } => point?.dataLabel !== undefined && point.plotY !== undefined);
	if (ends.length !== 2) return;
	const [upper, lower] = ends.sort((a, b) => a.plotY - b.plotY);
	const upperHeight = upper.dataLabel.getBBox().height;
	const lowerHeight = lower.dataLabel.getBBox().height;
	// The lower label always goes below its dot: above it, it sits on the upper line, which comes down to the
	// upper dot from the left (found 2026-09-30: "€106" on the occupancy line, rate dot 20 points lower).
	const below = lower.plotY + MARKER_RADIUS + END_LABEL_GAP;
	if (below + lowerHeight <= chart.plotHeight) {
		lower.dataLabel.attr({ y: below });
		return;
	}
	if (lower.dataLabel.translateY >= upper.dataLabel.translateY + upperHeight + END_LABEL_GAP) return;
	upper.dataLabel.attr({ y: lower.dataLabel.translateY - upperHeight - END_LABEL_GAP });
};
